import React, { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  CheckCircle,
  Download,
  FileText,
  Loader2,
  XCircle,
  Clock,
  ArrowRight,
  RefreshCw,
  Mail,
  Shield,
} from "lucide-react";
import axios from "axios";
import getApiBaseUrl from "../utils/apiBaseUrl";
import { useI18n } from "../i18n";
import { buttonClass } from "../components/site";

const BACKEND_URL = getApiBaseUrl();
const ORDER_ID_STORAGE_KEY = "optivra_guide_order_id";

const GuideSuccess = () => {
  const [searchParams] = useSearchParams();
  const queryOrderId = searchParams.get("order_id");
  const persistedOrderId = typeof window !== "undefined" ? localStorage.getItem(ORDER_ID_STORAGE_KEY) : "";
  const orderId = useMemo(() => queryOrderId || persistedOrderId || "", [queryOrderId, persistedOrderId]);

  const [status, setStatus] = useState("loading"); // loading, paid, pending, failed, expired
  const [orderData, setOrderData] = useState(null);
  const [downloadLinks, setDownloadLinks] = useState(null);
  const [error, setError] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [hasAutoDownloaded, setHasAutoDownloaded] = useState(false);

  const checkStatus = async () => {
    if (!orderId) {
      setStatus("failed");
      setError("No order ID found. Please check your payment email for order details.");
      return;
    }

    localStorage.setItem(ORDER_ID_STORAGE_KEY, orderId);

    try {
      const res = await axios.get(`${BACKEND_URL}/guide/payment-status/${orderId}`);
      const data = res.data;

      if (data.status === "paid") {
        setStatus("paid");
        setOrderData(data);
        await fetchDownloadLinks(orderId);
      } else if (data.status === "failed") {
        setStatus("failed");
        setError(data.message || "Payment failed. Please try again.");
      } else {
        setStatus("pending");
        setRetryCount((prev) => prev + 1);
      }
    } catch (err) {
      setStatus("failed");
      setError(err.response?.data?.detail || "Unable to verify payment status.");
    }
  };

  const fetchDownloadLinks = async (targetOrderId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/guide/download-links`, {
        params: { order_id: targetOrderId },
      });
      setDownloadLinks(response.data);
    } catch (err) {
      if (err.response?.status === 410) {
        setStatus("expired");
        setError("Download links have expired after 24 hours.");
      } else {
        setError(err.response?.data?.detail || "Unable to fetch download links.");
      }
    }
  };

  useEffect(() => {
    checkStatus();
    // eslint-disable-next-line
  }, [orderId]);

  useEffect(() => {
    if (status === "paid" && downloadLinks?.pdf && !hasAutoDownloaded && !downloading) {
      setHasAutoDownloaded(true);
      handleDownload(downloadLinks.pdf, "Placement_Success_Blueprint.pdf");
    }
    // eslint-disable-next-line
  }, [status, downloadLinks, hasAutoDownloaded, downloading]);

  useEffect(() => {
    if (status === "pending" && retryCount < 10) {
      const timer = setTimeout(() => {
        checkStatus();
      }, 3000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line
  }, [status, retryCount]);

  const handleDownload = async (url, filename) => {
    if (!url) return;
    setDownloading(true);
    try {
      const response = await axios.get(`${BACKEND_URL.replace(/\/api$/, "")}${url}`, {
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError("Download failed. Please try again or contact support.");
    }
    setDownloading(false);
  };

  const { t } = useI18n();
  const successErrors = t("success.errors");
  const tErr = (msg) => (msg && successErrors[msg]) || msg;

  return (
    <>
      <Header />

      <section className="page-in pt-36 pb-24 px-5 min-h-[80vh]">
        <div className="max-w-xl mx-auto">
          {status === "loading" && (
            <div className="text-center py-20">
              <Loader2 size={48} className="animate-spin text-brand mx-auto mb-6" />
              <h2 className="text-2xl font-semibold text-ink mb-2">{t("success.verifying")}</h2>
            </div>
          )}

          {status === "pending" && (
            <div className="text-center py-20">
              <Clock size={48} className="text-warn mx-auto mb-6" />
              <h2 className="text-2xl font-semibold text-ink mb-2">{t("success.pending")}</h2>
              <div className="flex items-center justify-center gap-2 text-ink-3 text-sm">
                <Loader2 size={14} className="animate-spin" />
                <span>{t("success.checking", { n: retryCount })}</span>
              </div>
              <button
                onClick={() => {
                  setRetryCount(0);
                  checkStatus();
                }}
                className={`${buttonClass("secondary")} mt-6`}
              >
                <RefreshCw size={16} />
                {t("success.checkAgain")}
              </button>
            </div>
          )}

          {status === "paid" && orderData && (
            <div>
              <div className="pop-in text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-soft mb-6">
                  <CheckCircle size={32} className="text-brand" />
                </div>
                <h1 className="font-display text-4xl md:text-5xl text-ink mb-2">{t("success.paid")}</h1>
              </div>

              <div className="pop-in rounded-3xl border border-line bg-surface p-6 mb-4">
                <h3 className="text-lg font-semibold text-ink mb-4 flex items-center gap-2">
                  <Download size={18} className="text-brand" />
                  {t("success.downloads")}
                </h3>

                <button
                  onClick={() => handleDownload(downloadLinks?.pdf, "Placement_Success_Blueprint.pdf")}
                  disabled={downloading || !downloadLinks?.pdf}
                  className={`${buttonClass("primary", "lg")} w-full mb-3`}
                >
                  {downloading ? <Loader2 size={20} className="animate-spin" /> : <Download size={20} />}
                  {t("success.dlGuide")}
                </button>

                <button
                  onClick={() => handleDownload(downloadLinks?.invoice, `invoice_${orderData.invoice_number || "download"}.pdf`)}
                  disabled={downloading || !downloadLinks?.invoice}
                  className={`${buttonClass("secondary")} w-full`}
                >
                  <FileText size={18} />
                  {t("success.dlInvoice")}
                </button>
              </div>

              <div className="rounded-2xl bg-paper border border-line p-4 mb-8">
                <div className="flex items-start gap-3">
                  <Shield size={16} className="text-ink-3 mt-0.5 flex-shrink-0" />
                  <p className="text-ink-2 text-sm">{t("success.linksNote")}</p>
                </div>
              </div>
            </div>
          )}

          {(status === "failed" || status === "expired") && (
            <div className="text-center py-20">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-danger-soft mb-6">
                <XCircle size={32} className="text-danger" />
              </div>
              <h2 className="font-display text-4xl text-ink mb-2">{status === "expired" ? t("success.expired") : t("success.failed")}</h2>
              <p className="text-ink-2 mb-8 max-w-md mx-auto">{tErr(error || "Something went wrong.")}</p>
              <Link
                to="/contact"
                className={buttonClass("primary")}
              >
                <Mail size={18} />
                {t("success.support")}
              </Link>
            </div>
          )}

          <div className="text-center">
            <Link to="/" className="text-ink-2 hover:text-brand transition-colors text-sm inline-flex items-center gap-1">
              {t("success.home")}
              <ArrowRight size={14} className="rtl:rotate-180" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default GuideSuccess;
